// Who an appointment is with: one doctor, or a joint visit of several (one row per person, RES-3342).

import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'

export type PeopleContext = {
  isEditing: boolean
  // The doctor of the row being edited; the back end always keeps them.
  ownId: number | null
  // Already a joint visit, or one still waiting for its OpenEMR group number.
  rowIsJoint: boolean
}

export type PeopleFields = { appointment_with?: number[]; primary?: number }

type EditedPeople = Partial<Pick<IPatientSchedule, 'consultation_staff' | 'appointment_with'>>

export const initialPeopleOf = (
  edited: EditedPeople | undefined,
  isEditing: boolean,
  defaultStaffId: number | null | undefined,
): number[] => {
  if (!isEditing) return defaultStaffId ? [defaultStaffId] : []
  const listed = (edited?.appointment_with ?? []).map((person) => person.consultation_user_id)
  const own = edited?.consultation_staff
  return own && !listed.includes(own) ? [own, ...listed] : listed
}

const keepsOwn = (people: number[], { isEditing, rowIsJoint }: PeopleContext): boolean =>
  isEditing && (rowIsJoint || people.length > 1)

// The row's own doctor stays on a joint visit; an ordinary one may still swap its doctor.
export const normalizePeople = (next: number[], context: PeopleContext): number[] =>
  context.ownId && keepsOwn(next, context) && !next.includes(context.ownId)
    ? [context.ownId, ...next]
    : next

export const lockedPersonOf = (people: number[], context: PeopleContext): number | null =>
  keepsOwn(people, context) ? context.ownId : null

type PrimaryInput = {
  people: number[]
  context: PeopleContext
  // The form's consultation_staff, which picks the primary of a new visit.
  staffId: number | null | undefined
  // The primary OpenEMR keeps for an existing joint visit.
  jointPrimaryId: number | null
}

export const primaryOf = ({
  people,
  context,
  staffId,
  jointPrimaryId,
}: PrimaryInput): number | null => {
  if (people.length < 2) return null
  if (!context.isEditing) return staffId && people.includes(staffId) ? staffId : people[0]
  if (!context.rowIsJoint) return context.ownId
  return jointPrimaryId && people.includes(jointPrimaryId) ? jointPrimaryId : null
}

export const staffIdFor = (
  people: number[],
  context: PeopleContext,
  current: number | null | undefined,
): number | null => {
  if (context.isEditing && people.length > 1) return context.ownId
  return current && people.includes(current) ? current : people[0] ?? null
}

const sameSet = (left: number[], right: number[]): boolean =>
  left.length === right.length && left.every((id) => right.includes(id))

type FieldsInput = {
  people: number[]
  context: PeopleContext
  initial: number[]
  primaryId: number | null
}

// Unchanged people are never sent: the back end would read a list as a change.
export const peopleFields = ({
  people,
  context,
  initial,
  primaryId,
}: FieldsInput): PeopleFields => {
  if (!context.isEditing) {
    if (people.length < 2 || !primaryId) return {}
    return {
      appointment_with: [primaryId, ...people.filter((id) => id !== primaryId)],
      primary: primaryId,
    }
  }
  if (sameSet(people, initial)) return {}
  return people.length > 1 || context.rowIsJoint ? { appointment_with: people } : {}
}

export type SmsNote = { text: string; tone: 'info' | 'warn' }

type SmsInput = {
  people: number[]
  primaryId: number | null
  context: PeopleContext
  nameOf: (id: number) => string
}

// Only the primary's copy texts the patient on a joint visit.
export const smsNoteOf = ({ people, primaryId, context, nameOf }: SmsInput): SmsNote | null => {
  if (people.length < 2) return null
  if (!primaryId) return { text: 'No primary · every copy may text the patient', tone: 'warn' }
  const primaryName = nameOf(primaryId)
  if (context.isEditing && context.ownId !== primaryId)
    return {
      text: `No SMS from this copy · the patient is texted once, from ${primaryName}’s copy`,
      tone: 'info',
    }
  return { text: `One SMS per visit · sent from ${primaryName}’s copy`, tone: 'info' }
}
